import { stripeService } from '@/services/stripe';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import {
  FinalizeStatus,
  type AdyenPaymentResponse,
  type NuveiPaymentResponse,
  type StripePaymentResponse,
} from '@/types/payment';
import type { RedirectPaymentContext } from '@/types/context';
import { CheckoutUrlPerEnvironment } from '@/constants/urls';
import { StripeRedirectMethodsWithManualCancel } from '@/constants/payment';
import { getEnvVariable } from '@/utils/get-env-variable';
import { isMobileDevice } from '@/utils/device';
import { logError } from '@/utils/logger';
import {
  stripeInPageActionApmMethods,
  stripeInPageNextActionPaymentMethods,
  stripePaymentHandlerTabMethods,
} from '@/constants/stripe';

export const getApmDynamicPageOrigin = (): string => {
  // In `vite dev` the checkout runs on localhost while VITE_ENV still points at a
  // deployed domain, so the env-mapped origin would never match the local
  // window's origin and the postMessage relay in <ApmDynamicPage/> would be
  // dropped. Use the app's own origin in dev so the redirect tab works locally.
  // This branch is statically removed from production builds (import.meta.env.DEV).
  if (import.meta.env.DEV) {
    return window.location.origin;
  }
  // Ephemeral (OD) deployments run a production build under `VITE_ENV=ephemeral`,
  // which has no entry in the map. The checkout and the relay page are served from
  // the same host there, so the window's own origin is the correct target — without
  // this fallback the redirect tab opens `undefined/apm-dynamic-page`.
  return (
    CheckoutUrlPerEnvironment[getEnvVariable('VITE_ENV') as keyof typeof CheckoutUrlPerEnvironment] ??
    window.location.origin
  );
};

export const getTabUrlPreparedForRedirect = (): string => {
  return `${getApmDynamicPageOrigin()}/apm-dynamic-page`;
};

export const handleOpenTab = (url: string): Window | null => {
  return window.open(url, '_blank');
};

// Stripe methods that never use a hosted redirect tab: card / GPay / ApplePay
// run their 3DS `next_action` in-page (handled via `on3DSRequired`, separate
// forms), PIX renders its voucher/QR in-page (<CashAppRedirect/>), and Link
// authenticates in-page via Stripe's express element (<LinkPayForm/>).
//
// AMAZON_PAY intentionally stays OUT of this list. When Amazon Pay is
// selected from the DEFAULT method picker it renders as a Stripe
// `<PaymentElement>` and the shopper is redirected to Amazon in a new tab
// (the legacy flow). The ECE-based Amazon Pay button (used e.g. in
// PREFERRED's last-method branch) bypasses this check via
// `payFromExpressCheckout` in `runPayment` — see `CheckoutContent.tsx`.
const STRIPE_IN_PAGE_METHODS: PaymentMethodEnum[] = [
  PaymentMethodEnum.CREDIT_CARD,
  PaymentMethodEnum.GOOGLE_PAY,
  PaymentMethodEnum.APPLE_PAY,
  PaymentMethodEnum.PIX,
  PaymentMethodEnum.LINK,
];

export const isRequireToOpenTab = (
  provider: ProviderEnum,
  paymentMethod?: PaymentMethodEnum,
  isMobile: boolean = isMobileDevice()
): boolean => {
  if (provider === ProviderEnum.ADYEN || provider === ProviderEnum.NUVEI) {
    return true;
  }
  // Stripe redirect APMs (Amazon Pay, Klarna, iDEAL, Affirm, ...) navigate to
  // the provider in a new tab. That tab must be opened synchronously inside the
  // user gesture, otherwise Safari blocks the programmatic `window.open` and the
  // PaymentIntent is left in `requires_action` and gets cancelled.
  if (provider !== ProviderEnum.STRIPE || !paymentMethod) {
    return false;
  }
  if (STRIPE_IN_PAGE_METHODS.includes(paymentMethod)) {
    return false;
  }
  // Generic in-page-action APMs (e.g. Bizum) resolve their next action in-page
  // via `handleNextAction` with no hosted redirect URL — they never open a tab,
  // so pre-opening one here would only leak a blank tab we'd have to close.
  if (stripeInPageActionApmMethods.includes(paymentMethod)) {
    return false;
  }
  // Swish always runs `handleNextAction` in the top-level PAYMENT_HANDLER tab
  // (all devices) — its next_action's app deep link would navigate the iframe if
  // run in-page. Pre-open the tab on the gesture so Safari doesn't block it.
  if (stripePaymentHandlerTabMethods.includes(paymentMethod)) {
    return true;
  }
  // CASH_APP / UPI render an in-page QR on desktop, but on mobile they hand off
  // to a popup (PAYMENT_HANDLER route) and therefore still need the tab.
  if (stripeInPageNextActionPaymentMethods.includes(paymentMethod) && !isMobile) {
    return false;
  }
  return true;
};

/**
 * Whether the shopper needs a manual way out of the redirect-processing overlay.
 *
 * True only for Stripe's `afterpay_clearpay`. Its hosted page sends
 * `Cross-Origin-Opener-Policy: same-origin`, which disowns our tab handle, so
 * `tab.closed` reports `true` while the shopper is still paying and the flow
 * loses its automatic exit (see `StripePaymentHandler.poll`). Every other
 * redirect method — including Afterpay behind another PSP — keeps a usable
 * handle and resolves on tab close, so it must not offer a cancel the rest of
 * that flow doesn't expect.
 */
export const canCancelRedirectPaymentManually = (
  provider?: ProviderEnum,
  paymentMethod?: PaymentMethodEnum
): boolean => {
  if (provider !== ProviderEnum.STRIPE || !paymentMethod) {
    return false;
  }
  return StripeRedirectMethodsWithManualCancel.includes(paymentMethod);
};

const getRedirectDataAdyen = async (
  paymentResponse: AdyenPaymentResponse
): Promise<Partial<RedirectPaymentContext>> => {
  const { adyen } = paymentResponse;
  const { status } = adyen;
  if (status === FinalizeStatus.REDIRECT) {
    return {
      redirectUrl: adyen.action?.url ?? undefined,
    };
  }
  return {};
};

const getRedirectDataNuvei = async (
  paymentResponse: NuveiPaymentResponse
): Promise<Partial<RedirectPaymentContext>> => {
  const { nuvei } = paymentResponse;
  const { redirectUrl, status } = nuvei;
  if (status === FinalizeStatus.REDIRECT) {
    return {
      redirectUrl: redirectUrl ?? undefined,
    };
  }
  return {};
};

const getRedirectDataStripe = async (
  paymentResponse: StripePaymentResponse
): Promise<Partial<RedirectPaymentContext>> => {
  const { stripe } = paymentResponse;
  const { clientSecret, status } = stripe;

  if (status === FinalizeStatus.REDIRECT) {
    const redirectUrl = await stripeService.getUrlFromSecretClientIntent(clientSecret);
    return {
      redirectUrl: redirectUrl ?? undefined,
      clientSecret,
      provider: ProviderEnum.STRIPE,
    };
  }

  return {};
};

export const getRedirectPaymentData = async (
  paymentResponse: unknown,
  provider: ProviderEnum
): Promise<Partial<RedirectPaymentContext>> => {
  if (provider === ProviderEnum.STRIPE) {
    return await getRedirectDataStripe(paymentResponse as StripePaymentResponse);
  }
  if (provider === ProviderEnum.ADYEN) {
    return await getRedirectDataAdyen(paymentResponse as AdyenPaymentResponse);
  }
  if (provider === ProviderEnum.NUVEI) {
    return await getRedirectDataNuvei(paymentResponse as NuveiPaymentResponse);
  }

  logError('Unsupported provider in redirect payment data', undefined, { provider });
  return {};
};
